import { InvoiceViewBlock, PaymentActionButtons } from '@models/invoice.model';
import { TwoFactorAuthorization } from '@models/two-fa.models';
import { Moment } from 'moment';
import { LeadSourceColor } from '@models/lead-sources.model';

export interface IntegrationStatus {
	text: string;
	cssClass: string;
}

export interface Integrations {
	companyName: string;
	integration: string;
	shootproofCompanyName: string;
}

export interface ProductPackage {
	id?: number;
	quantity: number;
	price: number;
	taxType?: string;
}

export interface DiscountType {
	id: number;
	value: string;
}

export interface currencyDTO {
	currencyCode: string;
	name: string;
	symbol: string;
}

export interface CompanyCurrencyAndTaxes {
	currencyDTO: currencyDTO;
	isChanged: boolean;
	percentage: number;
	taxName: string;

	[propName: string]: any;
}

export interface InvoicePayment {
	action?: PaymentActionButtons;
	amountWithCurrency?: AmountWithCurrency;
	amount: number;
	due: Date | string;
	id?: number;
	editable: boolean;
	part?: boolean;
	showNoDateWarning?: boolean;
	clientName?: string;
	jobName?: string;
	number?: number;
	jobId?: number;
	writtenOff?: boolean;
	[propName: string]: any;
}

export interface PaymentToRecord {
	id: number;
	amount: number;
	paidOn: Date;
}

export interface JobClient {
	id: number;
	address: string;
	email: string;
	firstName: string;
	lastName: string;
	phone: string;
	notes: string;
	companyName: string;
	country: string;
	postcode: string;
	state: string;
	status: string;
	town: string;
	hasImage: boolean;
	shootLocation: string;
	businessNumber?: string;

	[propName: string]: any;
}

export interface ClientSearch {
	id: number;
	name: string;
}

export interface InvoiceJob {
	id: number;
	name: string;
	primaryContact: JobClient;
	[propName: string]: any;
}

export interface PaymentScheduleName {
	id?: number;
	name: string;

	[propName: string]: any;
}

export interface PaymentSchedule {
	name: string;
	[propName: string]: any;
}

export interface PaymentScheduleDate {
	showNoDateWarning: boolean;
	schedule: any;
	date: Date;
}

export interface ProductPackageTemplate {
	id?: number;
	name?: string;
	description?: string;
	price?: number;
	taxType?: string;

	[propName: string]: any;
}

export interface InvoiceContractData {
	contracts: Array<any>;
	contract: any;
	updatedContract?: any;
	chosenContract?: any;
}

export interface InvoiceQuestData {
	questionnaires: Array<any>;
	questionnaire: any;
	chosenQuestionnaire?: any;
}

export interface Company {
	address: string;
	banner: string;
	city: string;
	country: string;
	deleteBanner: any;
	deleteImage: any;
	email: string;
	emailSetting: any;
	logoUrl: string;
	image: string;
	name: string;
	number: string;
	phone: string;
	postcode: string;
	state: string;
	timezone: string;
	users: any;
}

export interface JobContract extends OptInAgreement {
	id: number;
	invoiceDTO: any;
	inLead: boolean;
	jobAttachments: Array<any>;
	jobId: number;
	jobName: string;
	leadId: number;
	name: string;
	photographerSignature: string;
	photographerSignedOn: string;
	primarySignature: string;
	secondarySignature: string;
	secondarySignedOn: string;
	sent: boolean;
	signed: boolean;
	signedOn: string;
	text: string;
}

export interface JobQuestionnarie extends OptInAgreement {
	firstQuoteId: any;
	id: number;
	name: string;
	questions: Array<Question>;
	sent: boolean;
	submitDate: string;
}

export interface Question {
	answerText: string;
	errorMessage: any;
	id: number;
	message: any;
	number: number;
	questionText: string;
}

export interface SelectedDateTime {
	date: Date;
	startTime: string;
	endTime: string;
}

export interface UserSubscription {
	monthlyDiscount: boolean;
	annualDiscount: boolean;
	subscribedAnnual: boolean;
	subscribedMonthly: boolean;
	annualPromo: boolean;
	monthlyPromo: boolean;
}

export interface Contract {
	text: string;
	templateId?: number;
	id?: number;
	name?: string;
	signed?: boolean;
	// We need that much optIn due to compatibility issues
	showOptInConsent?: boolean;
	showOtpInConsent?: boolean;
	showOtpInConsen?: boolean;
	optInConsentStatement?: any;
	optInConsent?: boolean;
	actions?: ActionList;
	archived?: boolean;
	inLead?: boolean;
	jobAttachments?: JobAttachment;
	jobFinishDate?: string;
	jobFinishTime?: string;
	jobId?: number;
	jobName?: string;
	jobStartDate?: string;
	jobStartTime?: string;
	leadId?: number;
	photographerSignature?: string;
	photographerSignedOn?: string;
	primaryContact?: JobClient;
	primarySignature?: string;
	secondarySignature?: string;
	secondarySignedOn?: string;
	sent?: string;
	signedOn?: string;
}

export interface Mail {
	emailSubject: string;
	emailText: string;
	emailTo: string;
	emailCc: string;
	attachmentsData?: any;
	templateId?: any;
	templateName?: string;

	[propName: string]: any;
}

export interface Job {
	id: number;
	name: string;
	startDate: string;
	finishDate: string;
	startTime: string;
	finishTime: string;
	secondaryContact: JobClient;
	primaryContact: JobClient;
	jobQuestionnaires: Array<JobQuestionnarie>;
	jobContracts: Array<JobContract>;
	nextPaymentInvoices: Array<JobInvoice>;
	payments: any;
	quotes: Array<JobQuotes>;
	mails: Array<JobMail>;
	hasInvoices: boolean;
	workflow: JobWorkflow;
	type: JobType;
	notes: string;
	location: string;
	currencySymbol: string;
	hasNotAccepted: boolean;
	hasDate: boolean;
	attachments: Array<JobAttachment>;
	showNewTaskId: boolean;
	transformedFromLead: boolean;
	doubleBookingJobs: boolean;
	doubleBookingLeads: boolean;
	hasNoDate?: boolean;
	oldWorkflow?: JobWorkflow;
	shootProofGalleries?: Array<any>;
	inLead?: boolean;
	leadId?: number;
	archived?: boolean;
	paymentScheduleId?: number;
	clientPortalLink?: string;
	shootLocation: string;
	allday?: boolean;
}

export interface JobMinInfo {
	name: string;
	startDate: string;
	finishDate: string;
	id: number;
	inLead: boolean;
	leadId: number;
	primaryContact: any;
	archived?: boolean;
}

// deprecated, use Payment instead
export interface JobInvoice {
	totalDue: number;
	balanceDue: number;
	invoiceId: number;
	paymentId: number;
	paymentDisplayId: string;
	paymentDueDate: string;
	nextPaymentId: number;
	nextPaymentDisplayId: string;
	nextPaymentDueDate: string;
	invoiceDisplayId: string;
	paidOn: string;
	currencySymbol: string;
	invoiceSent: string;
	writeOffDate: string;
	writeOff: boolean;
}

export interface JobQuotes {
	accepted: boolean;
	errorMessage: string;
	id: number;
	message: string;
	quoteId: string;
	sent: string;
	total: number;
	updatedAt: string;
}

export interface JobMail {
	id: number;
	toWhom: string;
	subject: string;
	status: string;
	date: string;
}

export interface JobWorkflow {
	id: number;
	name: string;
	tasks?: Array<any>;
}

export interface WorkflowEvent {
	calendarEventType: string;
	clientId?: number;
	endDate: string;
	endTime: string;
	eventId?: number;
	id: number;
	inLead?: boolean;
	jobId?: number;
	jobName?: string;
	leadId?: number;
	location?: string;
	name: string;
	sendEmails?: any;
	startDate: string;
	startTime: string;
	taskId?: number;
	timeZone?: any;
	type?: string;
	typeColor?: string;
}

export interface JobType {
	name: string;
	colour: string;
	id: number;
	workflow: {
		id: number;
		name: string;
	};
	default: boolean;
}

export interface JobAttachment {
	id: number;
	name: string;
	bytesSize: number;
	contentType: string;
	cratedTs: string;
}

export interface LeadProfile {
	id: number;
	job: Job;
}

export interface Lead extends Job {
	jobId: number;
	status: string;
	statusOnDate: any;
}

export interface LeadToEdit {
	id: number;
	jobDTO: Job;
}

export interface AccessTokens {
	access_token: string;
	refresh_token: string;

	[propName: string]: any;
}

export interface OptInAgreement {
	optInConsent: boolean;
	optInConsentStatement: string;
	showOptInConsent: boolean;

	[propName: string]: any;
}

export interface UserLoginData {
	username: string;
	password: string;
	grant_type: string;
	companyId?: string;
	sixDigitVerificationCode?: string;
}

export interface User {
	readonly id: number;
	readonly firstName: string;
	readonly lastName: string;
	readonly email: string;
	readonly enabled: boolean;
	readonly companyImage: string;
	readonly currency: UserCurrency;
	readonly timezone: string;
	readonly intercomCompany: IntercomCompany;
	readonly companyPromoCode: string;
	readonly admin: boolean;
	readonly hasAvatar: boolean;
	readonly isGdprEnabled: boolean;
	readonly registeredAt: string;
	readonly twoFactorAuthorization: TwoFactorAuthorization;
	termsOfUseAgree: boolean;
	[propName: string]: any;
}

export interface UserAvatar {
	base64content: string;
	name: string;
	contentType: string;
}

export interface SubscriptionPlan {
	plan: string;
	message: string;
	expires: string;
	freeDaysAccess: number;
}

export interface UserCurrency {
	name: string;
	symbol: string;
	currencyCode: string;
}

export interface IntercomCompany {
	id: number;
	name: string;
	email: string;
	createdAt: string;
	plan: string;
	jobsCount: number;
	invoiceCount: number;
	quoteCount: number;
	contractCount: number;
	questionnaireCount: number;
	companyPromoCode: string;
	subscriptionStatus: string;
}

export interface Client {
	address: string;
	businessNumber: string;
	companyName: string;
	consents: Array<Consent>;
	country: string;
	email: string;
	firstName: string;
	hasImage: boolean;
	id: number;
	lastName: string;
	notes: string;
	phone: string;
	postcode: string;
	state: string;
	status: string;
	town: string;
}

export interface SortCriteria {
	fieldName: string;
	sortAscending: boolean;
}

export interface PageRequest {
	filterBy?: string;
	filter?: string;
	paging: {
		pageSize: number;
		pageNumber: number;
		searchToken?: string;
	};
	sortCriteria?: SortCriteria;
	sortCriterias?: Array<SortCriteria>;
	search?: string;
}

export interface AdminUsersSearch extends PageRequest {
	sortCriteria: {
		fieldName:
			| 'lastLogin'
			| 'email'
			| 'firstName'
			| 'registeredAt'
			| 'lastName'
			| 'id';
		sortAscending: boolean;
	};
}

export interface AdminUsersResponse {
	items: Array<any>;
	searchToken: string;
	totalSize: 0;
	[propName: string]: any;
}

export interface Consent {
	contactId: number;
	contractId: number;
	createdAt: string;
	id: number;
	jobId: number;
	method: string;
	questionnaireId: number;
	text: string;
	isLead: boolean;
	methodToShow?: string;
	leadId: number;
}

export interface TimeSettingModel {
	data: TimeSettingsTransferObject;
	debugInfo: any;
	errorCode: any;
	errorMessage: string;
	result: string;
}

export interface TimeSettingsTransferObject {
	'user.settings.calendar.dateFormat': string;
	'user.settings.calendar.timeFormat': string;
	'user.settings.calendar.weekStartsOn': string;
}

export interface TimeSettingsObject {
	dateFormat: string;
	timeFormat: string;
	weekStartsOn: string;
	fundyIntegration: string;
}

export interface PanelsPositionObject {
	panelsPosition: Array<string>;
}

export interface CustomizedLabels {
	mainShootLabel: string;
	extraShootLabel: string;
	shootsLabel: string;
	businessNumberLabel: string;
}

export interface RevenueComparisonRangesObject {
	thisYear: GraphsFilterObject;
	lastYear: GraphsFilterObject;
	nextYear: GraphsFilterObject;
}

export interface GraphsFilterObject {
	fromDate: string;
	toDate: string;
	jobTypeId: number | null;
	rangeOffsetInHours?: number;
}

export interface GraphsTotalAmounts {
	lead: number | null;
	shoot: number | null;
	payments: number | null;
	paymentsDue: number | null;
}

export type LegendPattern = 'YEAR' | 'NAME';

export interface PieOptions {
	legendRowLength: number;
	pattern: LegendPattern;
}

export interface LegendConfig {
	needLegend: boolean;
	legendRowLength: number;
	id: string;
	pattern: LegendPattern;
}
export interface GraphConfig {
	shortPeriod: boolean;
	chartName:
		| 'lead_chart'
		| 'shoot_chart'
		| 'payment_chart'
		| 'revenue_chart'
		| 'lead_source_chart';
	currency?: string;
}

export interface ViewAllModalPayload {
	jobTypes: Array<JobType>;
	selectedFilters: GraphsFilterObject;
	minDate: Moment;
	maxDate: Moment;
	selectedRange: SelectedDateRange;
}

export interface SelectedDateRange {
	startDate: string;
	endDate: string;
}

export interface PieChartData {
	colors: {
		[prop: string]: string;
	};
	columns: Array<[string, number]>;
	clearData: Array<LeadSourceStatisticItem>;
}

export interface LeadSourceStatisticItem {
	colour: LeadSourceColor;
	count: number;
	proportionNumber: number;
	name: string;
}

export interface LeadSourceFullStatisticItem {
	colour: LeadSourceColor;
	count: number;
	name: string;
	proportion: string;
	proportionNumber: number;
	jobsCount: number;
	leadsCount: number;
	status: string;
	conversationRateNumber: number;
}

export interface BootstrapSettingsData {
	'user.settings.calendar.weekStartsOn': string;
	'user.settings.calendar.dateFormat': string;
	'company.settings.integration.fundy.state': string;
	'company.settings.dashboard.panels.position': {
		panels: Array<string>;
	};
	'company.settings.customizeLabels': CustomizedLabels;
	'user.settings.calendar.timeFormat': string;
	'company.settings.contactform.version': string;
	'user.settings.fundy.integrationState': string;
	'company.settings.integration.fundy.available': boolean;
	'company.settings.integration.pictime.available': boolean;
	'company.settings.integration.proSelect.state': string;
	'company.settings.integration.proSelect.available': boolean;
	'company.settings.payments.export.available': boolean;
	'company.settings.leads.export.available': boolean;
	'company.settings.jobs.export.available': boolean;
	'company.settings.questionnairebuilder': string;
	'company.settings.xerointegration.version': string;
	'company.settings.leadSource.available': boolean;
}

export interface BootstrapSettings {
	weekStartsOn: string;
	dateFormat: string;
	timeFormat: string;
	fundyIntegration: string;
	fundyAvailable: boolean;
	picTimeAvailable: boolean;
	contactFormVersion: string;
	proSelectIntegration: string;
	proSelectAvailable: boolean;
	paymentExportAvailable: boolean;
	leadExportAvailable: boolean;
	jobExportAvailable: boolean;
	questionnaireTemplatesVersion: string;
	customisedLabels: CustomizedLabels;
	xeroIntegration: string;
	leadSourcesAvailable: boolean;
}

export interface ContractsAndQuestionnaires {
	contract: Contract;
	contracts: Array<Contract>;
	questionnaire: JobQuestionnarie;
	questionnaires: Array<JobQuestionnarie>;
}

export interface Product {
	id: number;
	name: string;
	description: string;
	price: number;
	quantity: any;
	discount: number;
	taxType: string;
	chosen?: boolean;
	type: string;
	order?: number;
	amount?: number;
	subTotal?: number;
	recommended: boolean;
	mandatory: boolean;
	taxValue?: number;
	discountValue?: number;
	detailsShown?: boolean;
	isSingle?: boolean;
	summary?: Summary;
	[prop: string]: any;
}

export interface QuotePayments {
	id: number;
	amount: number;
	paymentDate: string;
}

export interface Quotes {
	contract: Contract | null;
	currencyAndTaxes?: CompanyCurrencyAndTaxes;
	declineReason?: string;
	id?: number;
	introduction: string;
	issueDate: string;
	job?: JobClient;
	notes: '';
	paymentsSchedule: PaymentSchedule;
	payments: Array<QuotePayments>;
	ponumber: string | null;
	products: Array<Product>;
	questionnaire: JobQuestionnarie;
	quoteId: string;
	sentOn: string;
	status: string;
	summary?: Summary;
	[prop: string]: any;
}

export type QuoteType = 'fixed' | 'custom';

export interface Summary {
	taxesName?: string;
	discountValue?: number;
	discountValueWithCurrency?: AmountWithCurrency;
	items?: Array<Product>;
	subTotal?: number;
	subTotalWithCurrency?: AmountWithCurrency;
	taxPercent?: number;
	taxValue?: number;
	price?: number;
	priceWithCurrency?: AmountWithCurrency;
	taxValueWithCurrency?: AmountWithCurrency;
	total?: number;
	totalWithCurrency?: AmountWithCurrency;
	subTotalWithDiscount?: number;
	subTotalWithDiscountAndCurrency?: AmountWithCurrency;
}

export interface AmountWithCurrency {
	amount: number;
	amountWithCurrency: string;
	currencySymbol: string;
}

export interface Item {
	amount?: number;
	currencySymbol?: string;
	description?: string;
	discount?: number;
	id?: number;
	name?: string;
	order?: number;
	price?: number;
	summary?: Summary;
	taxType?: string;
	type?: string;
	quantity?: number;
	[prop: string]: any;
}

export interface IProductPackageItem {
	currencySymbol?: string;
	description: string;
	id?: number;
	name: string;
	price?: number | string;
	priceWithCurrency?: AmountWithCurrency;
	taxType?: string;
	type: string;
	typesForConverting?: Array<string>;
}

export interface IModalAction<T> {
	action: string;
	props: T;
}

export interface Package {
	quantity: number;
	price: number;
	discount: number;
	taxType: string;
	type: string;
	summary: {
		total: number;
	};
}

export interface ContractQuestionData {
	questionnaireTemplates: Array<ListItem>;
	questionnaire: Question;
	contractTemplates: Array<ListItem>;
	contract: Contract;
}

export interface ListItem {
	id: number;
	name: string;
}

export interface CurrencyDTO {
	currencyCode?: string;
	name?: string;
	symbol?: string;
}

export interface shootProofSettingsToIntegrate {
	code: string;
	clientPortalText: string;
}

export interface shootProofSettingsToUpdate {
	updateUserDetailsFromSP?: boolean;
	clientPortalText: string;
}
export interface ShootProofGalleriesBrand {
	name: string;
	spBrandId: number;
	spHomeBrandThemeId: number;
}

export interface ShootProofGalleriesBrandSelected {
	name: string;
	spGalleryId: number;
}

export interface ClientProfile {
	contact: Contract;
	currencySymbol: string;
	gdprEnabled: boolean;
	jobs: Array<Job>;
	leads: Array<Lead>;
	mails: Array<Mail>;
	payments: Array<Payment>;
	totalInvoiced: AmountWithCurrency | number;
}

export interface Payment {
	balanceDue: number | null;
	balanceDueWithCurrency: AmountWithCurrency;
	currencySymbol: string | null;
	invoiceDisplayId: string;
	invoiceId: number;
	invoiceSent: string | null;
	nextPaymentDisplayId: string | null;
	nextPaymentDueDate: string | null;
	nextPaymentId: number;
	notes: string | null;
	paidOn: string | null;
	paymentDisplayId: string | null;
	paymentDueDate: string | null;
	paymentId: number | null;
	source: string;
	totalDue: number;
	totalDueWithCurrency: AmountWithCurrency;
	writeOff: boolean | null;
	writeOffDate: string | null;
}

export interface InvoiceButtonsList {
	SEND?: ButtonsStates;
	PREVIEW?: ButtonsStates;
	EDIT?: ButtonsStates;
	SAVE_AS_PDF?: ButtonsStates;
	DELETE?: ButtonsStates;
	PRINT?: ButtonsStates;
	COPY_TO_QB?: ButtonsStates;
	COPY_TO_XERO?: ButtonsStates;
}

export interface QuoteButtonsList {
	SEND?: ButtonsStates;
	PREVIEW?: ButtonsStates;
	EDIT?: ButtonsStates;
	SAVE_AS_PDF?: ButtonsStates;
	DELETE?: ButtonsStates;
	PRINT?: ButtonsStates;
	MARK_AS_ACCEPTED?: ButtonsStates;
	GENERATE_INVOICE?: ButtonsStates;
}

export interface PaymentButtonsList {
	WRITE_OFF?: ButtonsStates;
	UNDO_WRITE_OFF?: ButtonsStates;
	RECORD?: ButtonsStates;
	PAY_ONLINE?: ButtonsStates;
	VIEW_INVOICE?: ButtonsStates;
	SNED_RECEIPT?: ButtonsStates;
	MARK_UNPAID?: ButtonsStates;
}

export interface ButtonsStates {
	state: string;
	message: string;
}

export interface quillOptions {
	toolbar: {
		container: any;
		handlers: {
			link(value: boolean): void;
			size(value: string): void;
			variables?(value: string): void;
			attachments?(value: string): void;
			color(value: string): void;
			image(value: boolean): void;
		};
	};
	counter?: any;
	clipboard?: {
		matchVisual: boolean;
	};
	imageDrop: boolean;
}

export const COLOR_PICKER_OPTIONS: Array<string> = [
	'#000000',
	'#94999d',
	'#b9c0c6',
	'#d9d8de',
	'#ebeaef',
	'#f5f4fc',
	'#ffffff',
	'#680007',
	'#910005',
	'#ce000d',
	'#c8202d',
	'#d8505c',
	'#ec9aa0',
	'#f5ccd0',
	'#7d3713',
	'#b14d19',
	'#f37936',
	'#ee8146',
	'#f19f6f',
	'#fbc9ae',
	'#fce4d8',
	'#7f6a11',
	'#b59914',
	'#f4d03e',
	'#f7da65',
	'#f8e28c',
	'#feebb3',
	'#fdf6d9',
	'#426d37',
	'#5c9b4c',
	'#87d37b',
	'#92d681',
	'#ade29e',
	'#c6eabe',
	'#e6f4e3',
	'#175226',
	'#1f7335',
	'#26a65b',
	'#47ac66',
	'#6ec189',
	'#9ad4ac',
	'#d5eddf',
	'#1d4672',
	'#2464a2',
	'#3598db',
	'#509bdf',
	'#76b4e7',
	'#a0ccf1',
	'#d7eaf9',
	'#22315c',
	'#2a4280',
	'#446cb2',
	'#5974b9',
	'#7e95c9',
	'#a8b6dd',
	'#d7e3ef',
	'#2f134d',
	'#411769',
	'#663398',
	'#7245a2',
	'#926eb8',
	'#b49cd0',
	'#f2eff8',
	'#4f1142',
	'#681656',
	'#841d6e',
	'#9e498a',
	'#b678a7',
	'#cda6c5',
	'#e7d2e3'
];

export interface PaymentItem {
	accountingState: AccountingState;
	amountWithCurrency: AmountWithCurrency;
	availableActions: PaymentActionButtons;
	clientFullName: string;
	clientEmail: string;
	daysToDue: number;
	dueOn: string;
	id: number;
	invoiceDisplayId: string;
	invoiceId: number;
	jobName: string;
	paidOn: string | null;
	state: {
		name: string;
		label: string;
	};
	writtenOffOn: string | null;
}

export interface AccountingState {
	explanationText: string;
	status: 'DISCONNECTED' | 'NOT_COPIED' | 'FAILED_TO_COPY';
}

export interface PaymentsOverview {
	items: Array<PaymentItem>;
	searchToken: string | null;
	sortingFields: Array<string>;
	totalSize: number;
}

export interface AmountWithCurrencyResponse {
	data: AmountWithCurrency | null;
	result: string;
}

export interface IFilesObj {
	attachments: [any];
	filesSize: number;
}

export interface NavBreadcrumb {
	link: string;
	title: string;
}

export interface ModalData {
	action: string;
	props: any;
}

export interface IItemsOverviewGen<T> {
	items: Array<T>;
	searchToken: string | null;
	sortingFields: Array<string>;
	totalSize: number;
}

export interface ServerDataResponse<T> {
	result: string;
	data: T;
}

export interface ExportLeadsJobsResponse {
	exportState: string;
	percentageComplete: number;
}

export interface MaxMinDate {
	minDate: string;
	maxDate: string;
}

export interface MediaFileConfig {
	multiple: boolean;
	accept: string;
	maxFileSize: number;
	id: string;
}

export interface MediaResponse {
	id: string;
	data: FileData;
}

export interface FileData {
	name: string;
	contentType: string;
	size: number;
	base64: string;
}

export const DEFAULT_MEDIA_FILE_CONFIG: Partial<MediaFileConfig> = {
	multiple: false,
	accept: 'image/jpeg, image/png'
};

export enum MediaFilesId {
	CompanyLogo = 'CompanyLogo',
	CompanyBanner = 'CompanyBanner'
}

export interface GeneralAction {
	actionName: string;
	method: string;
	url: string;
	alertMessage: AlertAction;
	actionDescription: string;
	withWarningMessage?: AlertAction;
}

export enum ActionMethodEnum {
	ALERT_MODAL = 'ALERT_MODAL',
	FRAME = 'FRAME',
	MANUAL = 'MANUAL',
	POST = 'POST'
}

export interface AlertAction {
	title: string;
	message: string;
	errorCode?: number;
}

export interface GeneralActions {
	[prop: string]: GeneralAction;
}

export interface ActionButtonClick {
	action: GeneralAction;
	type: string;
}

export interface ActionButtonResolve {
	action: GeneralAction;
	type: string;
	payload: any;
}

export interface GmailReplyData {
	senderEmail: string;
	senderName: string;
	replySettingsMetadata: EmailReplyMeta;
	replyConfirmationHtml?: string | null;
}

export interface SmtpData {
	from: string;
	serverUsername: string;
	host: string;
	passwordMask: string;
	port: string;
	withSSL: boolean;
	replySettingsMetadata: EmailReplyMeta;
	replyConfirmationHtml?: string | null;
}

export interface EmailReplyMeta {
	replyAccountCredentialId: number;
	enabled: boolean;
	instruction: string;
	emailAddress: {
		username: string;
		domain: string;
	};
	actions: GeneralActions;
}

export interface SmtpForm {
	from: string;
	serverUsername: string;
	password: string;
	host: string;
	port: string;
	withSSL: boolean;
	username?: string;
	receiveReplies?: boolean;
	accountCredentialId?: number;
}

export interface PropsAction<T> {
	action: string;
	props: T;
}

export interface QuestionnaireData {
	id: number;
	name: string; // eslint-disable-line no-restricted-globals
	parserLink: string;
	sentDate: string;
	status: string; // eslint-disable-line no-restricted-globals
	fileDTO: string;
	optInConsent: {
		optInConsent: boolean;
		optInConsentStatement: string;
	};
	submitDate: string;
	submitState: string;
	actions: ActionList;
}

export interface ClientPortalLink {
	link: string;
}

export interface ActionList {
	[prop: string]: {
		url: string;
		method: string;
		actionName: string;
		actionDescription: string;
		alertMessage: {
			title: string;
			message: string;
			errorCode?: number;
		};
	};
}

export interface IntegrationsStatus {
	qbIntegration: IntegrationItem;
	xeroIntegration: IntegrationItem;
	shootProofIntegration: IntegrationItem;
	picTimeIntegration: IntegrationItem;
}

export interface IntegrationItem {
	actions: ActionList;
	accountName: string;
	connectedOrganisation: {
		actions: ActionList;
		connectedOrganisation: string;
	};
}

export interface XeroConnections {
	connections: Array<XeroConnectionItem>;
}

export interface XeroConnectionItem {
	actions: ActionList;
	connected: boolean;
	organisationName: string;
	paymentAccounts: Array<XeroOptionsItem>;
	revenueAccounts: Array<XeroOptionsItem>;
}

export interface XeroOptionsItem {
	accountCode: string;
	name: string;
	selected: boolean;
}

export interface XeroConnectPayload {
	revenueAccountCode: string;
	paymentAccountCode?: string;
}

export interface GenericModalContent {
	title?: string;
	description: string;
	buttons: Array<GenericButton>;
}

export interface GenericButton {
	text: string;
	color: 'default' | 'general' | 'success' | 'info' | 'danger';
	size: 'lg' | 'md' | 'sm';
	action: string;
}

export interface TableHeaderItem {
	text: string;
	sortingCriteria: string;
	isSortable: boolean;
}

export interface PicTimeDataResponse {
	integrationUrl?: string;
}

export interface CreatePicTimeGallery {
	galleryName: string;
	virtualPath: string;
}

export interface PicTimeGalleriesResponse {
	actions: ActionList;
	lastSyncTime: string;
	name: string;
	numAllPhotos: number;
	redirectUrl: string;
}

export interface PicTimeGalleriesForImport {
	name: string;
	numAllPhotos: number;
	projectId: string;
}

export interface PicTimeActions {
	ptGalleryLayout: AvailableActions;
	state: string;
}

export interface AvailableActions {
	AvailableActions: string[];
}

export interface JobMetadata {
	name: string;
	galleryVirtualPath: string;
	mainShootDate: string;
}

export interface PreFillPicTimeData {
	accountName: string;
	clientPortalText: string;
}

export interface GetRedirectPicTimeUrl {
	configureGalleryUrl: string;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./shared/models/models.ts